import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import './T2ReviewPage.css';

/**
 * Auditor-facing review page — opened via the single-use token link in the email
 * sent by /api/opo/t2/package/:id/send. Mounted at /opo/t2/review/:token.
 *
 * The auditor:
 *   • Reviews the package metadata (sha256, file count, manifest)
 *   • Reviews each document with accept / reject / needs_info + comment
 *   • Downloads the ZIP or the manifest PDF
 *   • Submits the T2 (records reviewer confirmation in t2_auditor_invites.submitted_at)
 */
const DECISION_OPTIONS = [
  { id: 'accepted', label: 'Accept', cls: 't2r-decision-accept' },
  { id: 'needs_info', label: 'Need info', cls: 't2r-decision-info' },
  { id: 'rejected', label: 'Reject', cls: 't2r-decision-reject' },
];

const T2ReviewPage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [commentDraft, setCommentDraft] = useState({});
  const [savingItem, setSavingItem] = useState(null);
  const [filter, setFilter] = useState('all');
  const [sectionFilter, setSectionFilter] = useState('all');
  const [showSubmit, setShowSubmit] = useState(false);
  const [confirmationText, setConfirmationText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState(null);

  const reload = useCallback(() => {
    setLoading(true);
    fetch(`/api/opo/t2/auditor/invite/${token}`)
      .then((r) => r.ok ? r.json() : r.json().then((e) => Promise.reject(new Error(e.error || 'Could not load invite'))))
      .then((d) => setData(d))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { reload(); }, [reload]);

  const submitReview = (itemKey, decision) => {
    const comment = commentDraft[itemKey] || '';
    setSavingItem(itemKey);
    fetch(`/api/opo/t2/auditor/invite/${token}/review`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ item_key: itemKey, decision, comment }),
    })
      .then((r) => r.ok ? r.json() : r.json().then((e) => Promise.reject(new Error(e.error || 'Save failed'))))
      .then(() => reload())
      .catch((e) => setError(e.message))
      .finally(() => setSavingItem(null));
  };

  const submitT2 = () => {
    setSubmitting(true);
    setSubmitMessage(null);
    fetch(`/api/opo/t2/auditor/invite/${token}/submit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ confirmation_text: confirmationText }),
    })
      .then((r) => r.ok ? r.json() : r.json().then((e) => Promise.reject(new Error(e.error || 'Submit failed'))))
      .then(() => {
        setSubmitMessage('Submission recorded. The company has been notified.');
        setShowSubmit(false);
        reload();
      })
      .catch((e) => setSubmitMessage('Error: ' + e.message))
      .finally(() => setSubmitting(false));
  };

  const filteredItems = useMemo(() => {
    if (!data?.manifest?.items) return [];
    return data.manifest.items.filter((it) => {
      if (sectionFilter !== 'all' && it.sectionId !== sectionFilter) return false;
      if (filter === 'present' && it.status !== 'present') return false;
      if (filter === 'missing' && it.status !== 'missing') return false;
      if (filter === 'critical' && it.priority !== 'critical') return false;
      if (filter === 'pending_review') {
        const review = data.reviews[it.itemKey];
        if (review && review.decision) return false;
      }
      return true;
    });
  }, [data, filter, sectionFilter]);

  const sectionIds = useMemo(() => {
    if (!data?.manifest?.items) return [];
    return [...new Set(data.manifest.items.map((i) => i.sectionId))].sort();
  }, [data]);

  const reviewStats = useMemo(() => {
    if (!data?.manifest?.items) return null;
    const items = data.manifest.items;
    const reviews = data.reviews || {};
    const reviewed = items.filter((i) => reviews[i.itemKey]).length;
    const accepted = items.filter((i) => reviews[i.itemKey]?.decision === 'accepted').length;
    const rejected = items.filter((i) => reviews[i.itemKey]?.decision === 'rejected').length;
    const needsInfo = items.filter((i) => reviews[i.itemKey]?.decision === 'needs_info').length;
    return { total: items.length, reviewed, accepted, rejected, needsInfo };
  }, [data]);

  if (loading) return <div className="t2r-loading">Loading T2 package…</div>;
  if (error) return <div className="t2r-error">{error}</div>;
  if (!data) return <div className="t2r-error">No data.</div>;

  const { invite, package: pkg, reviews } = data;
  const submitted = invite.submittedAt != null;
  const expired = new Date(invite.expiresAt).getTime() < Date.now();

  return (
    <div className="t2r-page">
      <header className="t2r-header">
        <div>
          <div className="t2r-eyebrow">OptiPrime AI Inc. — T2 Corporate Return Review</div>
          <h1>FYE {pkg.taxYearEnd}</h1>
          <div className="t2r-meta">
            Reviewer: <strong>{invite.auditorName || invite.auditorEmail}</strong>
            {' · '}Package SHA-256: <code>{pkg.sha256?.slice(0, 16)}…</code>
            {' · '}{pkg.fileCount} files
            {' · '}{(pkg.totalBytes / (1024 * 1024)).toFixed(2)} MB
            {' · '}Expires {new Date(invite.expiresAt).toLocaleDateString()}
          </div>
        </div>
        <div className="t2r-header-actions">
          <a className="t2r-btn-secondary" href={pkg.downloadUrl}>Download ZIP</a>
          <a className="t2r-btn-secondary" href={pkg.manifestUrl} target="_blank" rel="noreferrer">Manifest PDF</a>
          {!submitted && !expired && (
            <button type="button" className="t2r-btn-primary" onClick={() => setShowSubmit(true)}>
              Submit T2 with CRA
            </button>
          )}
          {submitted && (
            <span className="t2r-submitted-badge">✓ Submitted {new Date(invite.submittedAt).toLocaleString()}</span>
          )}
          {expired && !submitted && <span className="t2r-expired-badge">⚠ Expired</span>}
        </div>
      </header>

      {reviewStats && (
        <div className="t2r-stats">
          <div className="t2r-stat">
            <div className="t2r-stat-num">{reviewStats.reviewed}/{reviewStats.total}</div>
            <div className="t2r-stat-lbl">Items reviewed</div>
          </div>
          <div className="t2r-stat t2r-stat--accept">
            <div className="t2r-stat-num">{reviewStats.accepted}</div>
            <div className="t2r-stat-lbl">Accepted</div>
          </div>
          <div className="t2r-stat t2r-stat--info">
            <div className="t2r-stat-num">{reviewStats.needsInfo}</div>
            <div className="t2r-stat-lbl">Needs info</div>
          </div>
          <div className="t2r-stat t2r-stat--reject">
            <div className="t2r-stat-num">{reviewStats.rejected}</div>
            <div className="t2r-stat-lbl">Rejected</div>
          </div>
        </div>
      )}

      <div className="t2r-filters">
        <select value={sectionFilter} onChange={(e) => setSectionFilter(e.target.value)}>
          <option value="all">All sections</option>
          {sectionIds.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        {['all', 'present', 'missing', 'critical', 'pending_review'].map((f) => (
          <button
            key={f}
            type="button"
            className={`t2r-filter-btn${filter === f ? ' is-active' : ''}`}
            onClick={() => setFilter(f)}
          >
            {f.replace('_', ' ')}
          </button>
        ))}
      </div>

      <ul className="t2r-items">
        {filteredItems.map((it) => {
          const r = reviews?.[it.itemKey];
          return (
            <li key={it.itemKey} className={`t2r-item t2r-item--${it.status}${r ? ' t2r-item--reviewed' : ''}`}>
              <div className="t2r-item-head">
                <div>
                  <div className="t2r-item-title">{it.document}</div>
                  <div className="t2r-item-sub">
                    <span className={`t2r-pri t2r-pri--${it.priority}`}>{it.priority}</span>
                    {' · '}{it.sectionId}{' · '}{it.phase}
                    {' · '}<code>{it.files?.[0]?.relPath || '(missing)'}</code>
                  </div>
                </div>
                <div className="t2r-item-status">
                  {it.status === 'present' ? '✓ Present' : '✗ Missing'}
                </div>
              </div>
              {r && (
                <div className={`t2r-review-existing t2r-review-${r.decision}`}>
                  <strong>Your decision: {r.decision.replace('_', ' ')}</strong>
                  {r.comment && <div className="t2r-review-comment">"{r.comment}"</div>}
                  <div className="t2r-review-date">{new Date(r.created_at).toLocaleString()}</div>
                </div>
              )}
              {!submitted && !expired && (
                <div className="t2r-item-controls">
                  <input
                    type="text"
                    className="t2r-comment-input"
                    placeholder="Optional comment for the company…"
                    value={commentDraft[it.itemKey] ?? r?.comment ?? ''}
                    onChange={(e) => setCommentDraft({ ...commentDraft, [it.itemKey]: e.target.value })}
                  />
                  {DECISION_OPTIONS.map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      className={`t2r-btn-decision ${opt.cls}${r?.decision === opt.id ? ' is-active' : ''}`}
                      onClick={() => submitReview(it.itemKey, opt.id)}
                      disabled={savingItem === it.itemKey}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {showSubmit && (
        <div className="t2r-modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowSubmit(false)}>
          <div className="t2r-modal">
            <h2>Submit T2 Corporate Return</h2>
            <p>
              By submitting, you confirm that you have reviewed every item, that the financial statements are
              accurate, and that you are filing the T2 with CRA on behalf of OptiPrime AI Inc. You will be
              recorded as the submitting auditor in the company's compliance records.
            </p>
            <p className="t2r-warning">
              ⚠ This action cannot be undone. {reviewStats?.rejected > 0 && <strong>You have {reviewStats.rejected} rejected items.</strong>}
            </p>
            <label className="t2r-modal-label">
              Your CPA licence number or other confirmation (recorded for audit trail):
              <input
                type="text"
                value={confirmationText}
                onChange={(e) => setConfirmationText(e.target.value)}
                placeholder="e.g. CPA #12345 — Jane Smith"
              />
            </label>
            <div className="t2r-modal-actions">
              <button type="button" className="t2r-btn-secondary" onClick={() => setShowSubmit(false)} disabled={submitting}>
                Cancel
              </button>
              <button type="button" className="t2r-btn-primary" onClick={submitT2} disabled={submitting || confirmationText.length < 4}>
                {submitting ? 'Submitting…' : 'Confirm and submit'}
              </button>
            </div>
            {submitMessage && <div className="t2r-modal-message">{submitMessage}</div>}
          </div>
        </div>
      )}
    </div>
  );
};

export default T2ReviewPage;
